import { yupResolver } from "@hookform/resolvers/yup";
import { Typography, Stack, IconButton, TextField, Grid, Autocomplete, Box, Button, Divider, } from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers";
import { CloseIcon } from "components/Icons"
import SaveIcon from '@mui/icons-material/SaveOutlined'
import { BasePathProvider } from "components/Providers"
import PhoneField from "components/PhoneField"
import dayjs from "dayjs";
import { Controller, useForm } from "react-hook-form";
import * as yup from 'yup'
import { SUFFIX_OPTIONS, GRADE_LEVELS, PHONE_TYPE_OPTIONS } from "constants";
import { PHONE_REGEXP } from "utils/phone"
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { save } from "store/application/student-applications";
import _ from 'lodash'
import { useEffect } from "react";

function StudentDetailsForm({ applicationRecord, setOpenDrawer, setApplicationRecord, academicYears = [] }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const staffConfirmationOptions = [
        { label: 'Staff Confirmed', value: true },
        { label: 'Staff Ineligible', value: false },
    ];

    const schema = yup.object().shape({
        firstName: yup.string().required(t('First Name is required')),
        lastName: yup.string().required(t('Last Name is required')),
        dateOfBirth: yup.date().required(t('Date of Birth is required')).typeError(t('Date of Birth is required')),
        gradeApp: yup.string().required(t('Grade Applying is required')),
        parentEmail: yup.string().email(t('Invalid email format')).nullable().notRequired(),
        parentPhone: yup
            .string()
            .matches(PHONE_REGEXP, { message: t("Enter a valid phone number"), excludeEmptyString: true })
            .nullable()
            .notRequired(),
        sibling: yup.boolean().required(t('Sibling attending is required')).nullable(),
        siblingFirstName: yup
            .string().when("sibling", {
                is: true,
                then: schema => schema.required(t("Sibling first name is required")).nullable(),
                otherwise: schema => schema.notRequired().nullable()
            }),
        siblingLastName: yup
            .string().trim().when("sibling", {
                is: true,
                then: schema => schema.required(t("Sibling last name is required")).nullable(),
                otherwise: schema => schema.notRequired().nullable()
            }),
        siblingGrade: yup.string().trim().when("sibling", {
            is: true,
            then: schema => schema.required(t("Sibling grade is required")).nullable(),
            otherwise: schema => schema.notRequired().nullable()
        }),
        staffPriority: yup.boolean().nullable().notRequired(),
        staffEmail: yup.string().trim().when("staffPriority", {
            is: true,
            then: schema => schema
                .required(t("Work email is required"))
                .email(t("Enter a valid email"))
                .nullable(),
            otherwise: schema => schema.notRequired().nullable()
        }),
    });

    const { control, handleSubmit, formState, watch, resetField } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            ...applicationRecord,
            gradeApp: applicationRecord?.studentGr?.id || '',
        }
    })


    const onSubmit = async (data) => {

        const exceptions = ['academicYearProvider', 'applicationSchools', 'studentGr', 'siblingGr', 'createdAt'];
        if (data.dateOfBirth) {
            data.dateOfBirth = dayjs(data.dateOfBirth).format('YYYY-MM-DD');
        }
        if (data.address.id) {
            exceptions.push('address.id');
        }

        const payload = _.omit(data, exceptions);
        const id = data.id;

        try {
            const res = await dispatch(save({ id, ...payload }));

            if (res.payload) {
                setApplicationRecord(prev => ({
                    ...prev,
                    ...res.payload
                }))
                setOpenDrawer(false);
            } else {
                console.error('Save failed:', res);
            }
        } catch (error) {
            console.error('Save error:', error);
        }
    };

    const yesNoOptions = [
        { label: t('Yes'), value: true },
        { label: t('No'), value: false }
    ];


    const hasSibling = watch("sibling");
    const hasStaffPriority = watch("staffPriority");

    useEffect(() => {
        if (hasSibling === false || hasSibling === '') {
            resetField("siblingFirstName", { defaultValue: null });
            resetField("siblingLastName", { defaultValue: null });
            resetField("siblingGrade", { defaultValue: null });
        }
    }, [hasSibling, resetField]);

    useEffect(() => {
        if (hasStaffPriority === false || hasStaffPriority === '') {
            resetField("staffEmail", { defaultValue: null });
        }
    }, [hasStaffPriority, resetField]);

    return (
        <BasePathProvider value={() => setOpenDrawer(false)}>
            <Stack
                sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
            >
                <Typography variant="h6" fontWeight={300}>
                    {`Update ${applicationRecord?.firstName || ""} ${applicationRecord?.lastName || ""}'s Record`}
                </Typography>
                <IconButton aria-label="close" onClick={() => setOpenDrawer(false)}>
                    <CloseIcon />
                </IconButton>
            </Stack>
            <Grid container spacing={1.5} sx={{ p: 2 }} data-component="StudentDetailsForm" component="form" onSubmit={handleSubmit(onSubmit)}>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="firstName"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                size="small"
                                label="First Name"
                                error={!!formState.errors.firstName}
                                helperText={formState.errors.firstName?.message}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="middleName"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                size="small"
                                label="Middle Name"
                                error={!!formState.errors.middleName}
                                helperText={formState.errors.middleName?.message}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="lastName"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                size="small"
                                label="Last Name"
                                error={!!formState.errors.lastName}
                                helperText={formState.errors.lastName?.message}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="suffix"
                        control={control}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                options={SUFFIX_OPTIONS}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                value={SUFFIX_OPTIONS.find(opt => opt.value === field.value) || null}
                                onChange={(_, newValue) => field.onChange(newValue ? newValue.value : "")}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("Suffix")}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        size="small"
                                        fullWidth
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="dateOfBirth"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="Date of Birth"
                                fullWidth
                                size="small"
                                sx={{ width: '100%', size: 'small' }}
                                value={dayjs(field.value) || null}
                                onChange={field.onChange}
                                slotProps={{
                                    textField: {
                                        size: 'small'
                                    }
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="gradeApp"
                        control={control}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                options={GRADE_LEVELS}
                                getOptionLabel={(option) => option}
                                isOptionEqualToValue={(option, value) => option === value}
                                value={field.value || null}
                                onChange={(_, newValue) => field.onChange(newValue || "")}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Grade Applying"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        size="small"
                                        fullWidth
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="address.street"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                size="small"
                                label="Street Address"
                                error={!!formState.errors.address?.street}
                                helperText={formState.errors.address?.street?.message}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="address.line2"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                size="small"
                                label="Address Line 2"
                                error={!!formState.errors.address?.line2}
                                helperText={formState.errors.address?.line2?.message}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="address.city"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                size="small"
                                label="City"
                                error={!!formState.errors.address?.city}
                                helperText={formState.errors.address?.city?.message}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Controller
                        name="address.state"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                size="small"
                                label="State"
                                error={!!formState.errors.address?.state}
                                helperText={formState.errors.address?.state?.message}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Controller
                        name="address.zip"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                size="small"
                                label="Zip Code"
                                error={!!formState.errors.address?.zip}
                                helperText={formState.errors.address?.zip?.message}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="yearApp"
                        control={control}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                options={academicYears}
                                getOptionLabel={(option) => option?.label || ''}
                                isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                                value={academicYears.find(opt => String(opt.id) === String(field.value)) || null}
                                onChange={(_, newValue) => field.onChange(newValue ? newValue.id : null)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("Enrollment Year")}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        size="small"
                                        fullWidth
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="currentSchool"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={t("Current School")}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                size="small"
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sx={{ m: 1 }}>
                    <Divider>
                        <Typography variant="body1" fontWeight="bold">
                            {t('Student Priority Information')}
                        </Typography>
                    </Divider>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Controller
                        name="sibling"
                        control={control}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                options={yesNoOptions}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                value={yesNoOptions.find(opt => opt.value === field.value) || null}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue ? newValue.value : '');
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('Sibling Attending')}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                        size="small"
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                {hasSibling && (
                    <>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="siblingFirstName"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label={t("Sibling First Name")}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        size="small"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="siblingLastName"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label={t("Sibling Last Name")}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        size="small"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="siblingGrade"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <Autocomplete
                                        options={GRADE_LEVELS}
                                        getOptionLabel={(option) => option}
                                        isOptionEqualToValue={(option, value) => option === value}
                                        value={field.value || null}
                                        onChange={(_, newValue) => field.onChange(newValue || null)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t("Sibling Grade")}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                fullWidth
                                                size="small"
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                    </>
                )}
                <Grid item xs={12} md={6}>
                    <Controller
                        name="staffPriority"
                        control={control}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                options={yesNoOptions}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                value={yesNoOptions.find(opt => opt.value === field.value) || null}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue ? newValue.value : '');
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('Parent/Guardian Employed')}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                        size="small"
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                {hasStaffPriority && (
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="staffEmail"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    value={field.value ?? ''}
                                    fullWidth
                                    label={t("Work Email of the Employed Parent/Guardian")}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    size="small"
                                />
                            )}
                        />
                    </Grid>
                )}
                {hasStaffPriority && (
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="staffCheck"
                            control={control}
                            render={({ field, fieldState }) => (
                                <Autocomplete
                                    options={staffConfirmationOptions}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    value={staffConfirmationOptions.find(opt => opt.value === field.value) || null}
                                    onChange={(event, newValue) => {
                                        field.onChange(newValue ? newValue.value : '');
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('Staff Confirmation')}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            fullWidth
                                            size="small"
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                )}
                <Grid item xs={12} md={6}>
                    <Controller
                        name="edcStudent"
                        control={control}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                options={yesNoOptions}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                value={yesNoOptions.find(opt => opt.value === field.value) || null}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue ? newValue.value : '');
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('Economically Disadvantaged Student')}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                        size="small"
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="ellStudent"
                        control={control}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                options={yesNoOptions}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                value={yesNoOptions.find(opt => opt.value === field.value) || null}
                                onChange={(event, newValue) => {
                                    field.onChange(newValue ? newValue.value : '');
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('ELL Student')}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        fullWidth
                                        size="small"
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sx={{ m: 1 }}>
                    <Divider>
                        <Typography variant="body1" fontWeight="bold">
                            {t('Parent/Guardian Information')}
                        </Typography>
                    </Divider>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="parentFirstName"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={t("Parent First Name")}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                size="small"
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="parentLastName"
                        control={control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={t("Parent Last Name")}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                size="small"
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="type"
                        control={control}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                options={PHONE_TYPE_OPTIONS}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                value={PHONE_TYPE_OPTIONS.find(opt => opt.value === field.value) || null}
                                onChange={(_, newValue) => field.onChange(newValue ? newValue.value : "")}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("Phone Type")}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        size="small"
                                        fullWidth
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <PhoneField
                        name="parentPhone"
                        control={control}
                        label={t("Phone Number")}
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="parentEmail"
                        control={control}
                        render={({ field, fieldState }) => {
                            return (
                                <TextField
                                    {...field}
                                    fullWidth
                                    label={t("Parent Email")}
                                    error={!!fieldState.error}
                                    size="small"
                                    helperText={fieldState.error?.message}
                                />
                            )
                        }}
                    />
                </Grid>

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Button type="submit" variant="contained" startIcon={<SaveIcon />}>
                        {t('Submit')}
                    </Button>
                </Grid>
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                </Box>
            </Grid>
        </BasePathProvider>
    )
}

export default StudentDetailsForm